import { UploadOutlined } from "@ant-design/icons"
import { Form, Typography, Upload } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { UploadChangeParam } from "antd/es/upload/interface"
import React, { type ComponentProps } from "react"
import { useTranslation } from "react-i18next"

import { AttachmentsList } from "../../../../../components/AttachmentsList"
import { validateFile } from "../../../../../utils/file-validation.ts"
import { type ProjectFormData } from "../project-form-data.ts"

const { Text } = Typography

const MAX_SIZE_MB = 50
const ALLOWED_FILE_EXTENSIONS = [
  "png",
  "jpeg",
  "jpg",
  "pdf",
  "mp4",
  "mov",
  "webm",
  "doc",
  "docx"
]

interface AttachmentsSectionProps {
  initialValues?: Partial<ProjectFormData>
  form: FormInstance<ProjectFormData>
}

export function AttachmentsSection({
  initialValues,
  form
}: AttachmentsSectionProps) {
  const { t } = useTranslation()

  const deletedFiles = Form.useWatch("deleted_attachment_ids", form) || []
  const uploadedFiles = Form.useWatch("file_list", form) || []

  // const maxCount =
  //   20 - (initialValues?.["attachments"]?.length || 0) + deletedFiles?.length
  const attachments =
    initialValues?.["attachments"]?.filter(
      (file) => !file?.["is_archived"] && !deletedFiles?.includes(file?.id)
    ) || []

  const beforeUpload = (file: File) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS
    })
  }

  const handleDeletedFile = (file: { id: string }) => {
    form.setFieldValue("deleted_attachment_ids", [...deletedFiles, file?.id])
  }

  const getValueFromEvent = (e: File[] | UploadChangeParam) => {
    return Array.isArray(e) ? e : e?.fileList
  }

  const uploadProps: ComponentProps<typeof Upload> = {
    multiple: true,
    accept: ALLOWED_FILE_EXTENSIONS?.map((ext) => `.${ext}`).join(","),
    // maxCount,
    beforeUpload,
    onDrop: (e) => {
      const files = Array.from(e?.dataTransfer?.files)

      files.forEach((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase()
        if (!ALLOWED_FILE_EXTENSIONS.includes(ext || "")) {
          files.forEach(beforeUpload)
        }
      })
    }
  }

  return (
    <>
      <Form.Item
        label="Upload Documents"
        name="file_list"
        valuePropName="fileList"
        getValueFromEvent={getValueFromEvent}
        // rules={[
        //   {
        //     validator: (_, value) => {
        //       if (value && value.length > maxCount) {
        //         return Promise.reject(
        //           new Error(t("You can upload a maximum of 20 files."))
        //         )
        //       }
        //       return Promise.resolve()
        //     }
        //   }
        // ]}
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <Text>Click or drag files to this area to upload</Text>
          <br />
          <Text type="secondary">{`${ALLOWED_FILE_EXTENSIONS.join(", ")?.toUpperCase()} up to ${MAX_SIZE_MB}MB each`}</Text>
          <br />
          {uploadedFiles.length > 0 && (
            <Text
              type="secondary"
              strong
              style={{ display: "inline-block", marginTop: "0.5rem" }}
            >
              {uploadedFiles.length} file(s) selected
            </Text>
          )}
        </Upload.Dragger>
      </Form.Item>

      {!!attachments?.length && (
        <Form.Item>
          <AttachmentsList
            attachments={attachments}
            preview
            onDelete={handleDeletedFile}
          />
        </Form.Item>
      )}
    </>
  )
}
